import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BellRing, History, Loader2, Mail, MessageSquare, X } from 'lucide-react';
import api from '../lib/api';
import { ConfirmAction } from './PlatformPharmacies';

const DAY = 864e5;
const CH_COLOR = { sent: '#52e0b8', failed: '#ff9b9b', not_sent: '#b9c7d6' };
const CH_LABEL = { sent: 'Sent', failed: 'Failed', not_sent: 'Not sent' };
const stamp = (iso) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export const dueBranches = (tills) => {
  const out = new Map();
  tills.forEach(t => {
    const recent = t.reminded_at && Date.now() - new Date(t.reminded_at).getTime() < DAY;
    if (t.state === 'behind' && !recent && !out.has(t.tenant_id)) out.set(t.tenant_id, t.branch_name);
  });
  return [...out.values()];
};

export const RemindAllButton = ({ tills, onSent }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const due = dueBranches(tills);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/platform/installations/rollout/remind-all');
      toast.success(`Reminders sent to ${data.sent_count} branch(es)${data.skipped_count ? `, ${data.skipped_count} skipped` : ''}`);
      setOpen(false); onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send reminders'); }
    setBusy(false);
  };
  return (
    <>
      <button type="button" className="pc-btn" disabled={!due.length} onClick={() => setOpen(true)} data-testid="pc-rollout-remind-all"
        title={due.length ? undefined : 'Every branch that is behind was reminded in the last 24 hours'}>
        <BellRing className="h-3.5 w-3.5 inline mr-1" />Remind all ({due.length})
      </button>
      <ConfirmAction open={open} requireReason={false} busy={busy} testId="pc-rollout-remind-all-confirm" confirmLabel={`Send ${due.length} reminder(s)`}
        title={`Send update reminders to ${due.length} branch(es)?`}
        description="Each gets an email, an SMS (when a Ghana phone is on file) and a notice in their bell. Branches reminded in the last 24 hours are skipped."
        onCancel={() => setOpen(false)} onConfirm={send}>
        <ul className="pc-muted" style={{ maxHeight: '10rem', overflow: 'auto', paddingLeft: '1rem', listStyle: 'disc' }} data-testid="pc-rollout-remind-all-list">
          {due.map(n => <li key={n}>{n}</li>)}
        </ul>
      </ConfirmAction>
    </>
  );
};

const Channel = ({ Icon, name, ch, to, fixTenantId, reminderId }) => (
  <div style={{ display: 'flex', gap: '.4rem', alignItems: 'flex-start', fontSize: '.78rem' }}>
    <Icon className="h-3.5 w-3.5" style={{ marginTop: '.15rem', color: CH_COLOR[ch?.status] }} />
    <span><b style={{ color: CH_COLOR[ch?.status] }}>{name}: {CH_LABEL[ch?.status] || 'Unknown'}</b>{to ? ` to ${to}` : ''}
      <span className="pc-muted"> · {ch?.detail}</span>{fixTenantId && <Link className="pc-link" style={{ marginLeft: '.35rem' }}
        to={`/platform/installations/${fixTenantId}?contact=1`} data-testid={`pc-rollout-fix-contact-${reminderId}-${name.toLowerCase()}`}>Fix contact</Link>}</span>
  </div>
);

const HistoryModal = ({ till, onClose }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setError(false); setRows(null);
    api.get(`/platform/installations/rollout/reminders/${till.tenant_id}`).then(r => setRows(r.data.reminders)).catch(() => setError(true));
  }, [till.tenant_id]);
  return (
    <div className="pc-modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pc-modal" data-testid="pc-rollout-history">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.5rem' }}>
          <h3>Reminders · {till.branch_name}</h3>
          <button type="button" className="pc-btn" onClick={onClose} data-testid="pc-rollout-history-close"><X className="h-3.5 w-3.5" /></button>
        </div>
        {!rows && !error && <Loader2 className="h-4 w-4 animate-spin" data-testid="pc-rollout-history-loading" />}
        {error && <p className="pc-muted" data-testid="pc-rollout-history-error">Could not load reminder history.</p>}
        {rows?.length === 0 && <p className="pc-muted" data-testid="pc-rollout-history-empty">No reminders sent to this branch yet.</p>}
        <div style={{ maxHeight: '60vh', overflow: 'auto', display: 'grid', gap: '.7rem', marginTop: '.6rem' }}>
          {rows?.map(r => (
            <div key={r.reminder_id || r.sent_at} style={{ borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: '.6rem' }} data-testid={`pc-rollout-history-row-${r.reminder_id || r.sent_at}`}>
              <div style={{ color: '#fff', fontSize: '.85rem' }}>{stamp(r.sent_at)} · update to {r.version}{r.bulk ? ' · Remind all' : ''}</div>
              <div className="pc-muted" style={{ fontSize: '.75rem', marginBottom: '.3rem' }}>{r.till_count} till(s) on {r.old_versions} · by {r.sent_by}</div>
              {r.relabel_remaining > 0 && <div className="pc-muted" style={{ fontSize: '.75rem', marginBottom: '.3rem' }} data-testid={`pc-rollout-history-relabel-${r.reminder_id}`}>
                {r.relabel_remaining} pack(s) left to relabel
              </div>}
              <Channel Icon={Mail} name="Email" ch={r.email || { status: r.email_ok ? 'sent' : 'failed', detail: '' }} to={r.email_to}
                fixTenantId={r.contact_missing?.includes('email') ? r.contact_fix_tenant_id : ''} reminderId={r.reminder_id} />
              <Channel Icon={MessageSquare} name="SMS" ch={r.sms || { status: r.sms_ok ? 'sent' : 'not_sent', detail: '' }} to={r.sms_to}
                fixTenantId={r.contact_missing?.includes('ghana_phone') ? r.contact_fix_tenant_id : ''} reminderId={r.reminder_id} />
              <Channel Icon={BellRing} name="Bell" ch={r.bell || { status: 'sent', detail: "Shown in the pharmacy's bell" }} />
            </div>
          ))}
        </div>
        <p className="pc-muted" style={{ fontSize: '.7rem', marginTop: '.8rem' }}>"Sent" means the email or SMS provider accepted the message. The providers don't report back when it reaches an inbox or phone.</p>
      </div>
    </div>
  );
};

export const HistoryButton = ({ till }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="pc-btn" title="Reminder history" aria-label="Reminder history" onClick={() => setOpen(true)} data-testid={`pc-rollout-history-${till.tenant_id}`}>
        <History className="h-3.5 w-3.5 inline mr-1" />History
      </button>
      {open && <HistoryModal till={till} onClose={() => setOpen(false)} />}
    </>
  );
};
